/**
 * Agos API Client
 * Secure cookie-based authentication with CSRF protection
 * Tokens stored in httpOnly cookies (not accessible to JS/XSS)
 */

const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:3001');

// Types
export interface User {
  id: string;
  email: string;
  fullName: string | null;
  companyName: string | null;
  phone: string | null;
  role: 'user' | 'admin' | 'enterprise';
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  createdAt: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  details?: any[];
}

// ============================================
// CSRF Token Management
// ============================================

let csrfToken: string | null = null;

/**
 * Read CSRF token from the agos_csrf cookie (non-httpOnly, readable by JS)
 */
function getCsrfTokenFromCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)agos_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Fetch CSRF token from server if not available in cookie
 */
async function ensureCsrfToken(): Promise<string | null> {
  // Try cookie first
  csrfToken = getCsrfTokenFromCookie();
  if (csrfToken) return csrfToken;

  // Fetch from server
  try {
    const response = await fetch(`${API_URL}/api/csrf-token`, {
      credentials: 'include',
    });
    const data = await response.json();
    if (data.success && data.data?.csrfToken) {
      csrfToken = data.data.csrfToken;
      return csrfToken;
    }
  } catch {
    // CSRF fetch failed — will retry on next request
  }
  return null;
}

// Fetch CSRF token on module load
ensureCsrfToken();

// ============================================
// Auth State Tracking
// ============================================

// Track auth state in memory (cookies are httpOnly, can't read them)
let isLoggedIn = false;

// Token refresh handling
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Sends refresh_token cookie
      });

      if (!response.ok) {
        isLoggedIn = false;
        return false;
      }

      const data: ApiResponse = await response.json();
      if (data.success) {
        isLoggedIn = true;
        // Re-read CSRF token (may have been rotated)
        csrfToken = getCsrfTokenFromCookie();
        return true;
      }

      isLoggedIn = false;
      return false;
    } catch {
      isLoggedIn = false;
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ============================================
// API Request Helper
// ============================================

async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_URL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Add CSRF token for state-changing requests
  const method = (options.method || 'GET').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    // Ensure CSRF token is available before making state-changing requests
    let token = getCsrfTokenFromCookie() || csrfToken;
    if (!token) {
      // Token not available - fetch it first
      await ensureCsrfToken();
      token = getCsrfTokenFromCookie() || csrfToken;
    }
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  try {
    let response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Always send cookies
    });

    // If 401, try to refresh token
    if (response.status === 401 && isLoggedIn) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Re-read CSRF token after refresh
        const freshCsrf = getCsrfTokenFromCookie();
        if (freshCsrf && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
          headers['X-CSRF-Token'] = freshCsrf;
        }
        response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
        });
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
      code: 'NETWORK_ERROR',
    };
  }
}

// ============================================
// AUTH API
// ============================================

export const auth = {
  /**
   * Register a new user
   */
  async signUp(
    email: string,
    password: string,
    fullName?: string,
    companyName?: string
  ): Promise<{ user: User } | null> {
    const response = await apiRequest<{ user: User }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, fullName, companyName }),
    });

    if (response.success && response.data) {
      isLoggedIn = true;
      csrfToken = getCsrfTokenFromCookie();
      return response.data;
    }

    throw new Error(response.error || 'Registration failed');
  },

  /**
   * Login user
   */
  async signIn(
    email: string,
    password: string
  ): Promise<{ user: User } | null> {
    const response = await apiRequest<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success && response.data) {
      isLoggedIn = true;
      csrfToken = getCsrfTokenFromCookie();
      return response.data;
    }

    throw new Error(response.error || 'Login failed');
  },

  /**
   * Logout user
   */
  async signOut(): Promise<void> {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } finally {
      isLoggedIn = false;
    }
  },

  /**
   * Get current user
   */
  async getUser(): Promise<User | null> {
    const response = await apiRequest<{ user: User }>('/api/auth/me');
    if (response.success && response.data) {
      isLoggedIn = true;
      return response.data.user;
    }

    isLoggedIn = false;
    return null;
  },

  /**
   * Check if user is authenticated (in-memory flag, validated on next API call)
   */
  isAuthenticated(): boolean {
    return isLoggedIn;
  },

  /**
   * Check if admin exists (for initial setup)
   */
  async adminExists(): Promise<boolean> {
    const response = await apiRequest<{ exists: boolean }>('/api/auth/admin-exists');
    return response.data?.exists ?? false;
  },

  /**
   * Create admin user (initial setup)
   */
  async createAdmin(
    email: string,
    password: string,
    fullName: string
  ): Promise<User> {
    const response = await apiRequest<{ user: User }>('/api/auth/admin-setup', {
      method: 'POST',
      body: JSON.stringify({ email, password, fullName }),
    });

    if (response.success && response.data) {
      return response.data.user;
    }

    throw new Error(response.error || 'Failed to create admin');
  },
};

// ============================================
// MULTIPART UPLOAD HELPER
// ============================================

async function apiRequestMultipart<T = any>(
  endpoint: string,
  formData: FormData,
  guestToken?: string,
  handoffSessionId?: string
): Promise<ApiResponse<T>> {
  const url = `${API_URL}${endpoint}`;

  const headers: Record<string, string> = {};
  // Do NOT set Content-Type — browser sets multipart boundary automatically

  // Add CSRF token - ensure it's available before making request
  let token = getCsrfTokenFromCookie() || csrfToken;
  if (!token) {
    await ensureCsrfToken();
    token = getCsrfTokenFromCookie() || csrfToken;
  }
  if (token) {
    headers['X-CSRF-Token'] = token;
  }

  // Add guest token if provided
  if (guestToken) {
    headers['X-Guest-Token'] = guestToken;
  }

  // Add handoff session ID if provided (for mobile handoff uploads)
  if (handoffSessionId) {
    headers['X-Handoff-Session'] = handoffSessionId;
  }

  try {
    console.log('apiRequestMultipart:', { url, headers: Object.keys(headers), hasHandoffSession: !!handoffSessionId });
    let response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    // If 401, try to refresh token
    if (response.status === 401 && isLoggedIn) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        const freshCsrf = getCsrfTokenFromCookie();
        if (freshCsrf) headers['X-CSRF-Token'] = freshCsrf;
        response = await fetch(url, {
          method: 'POST',
          headers,
          body: formData,
          credentials: 'include',
        });
      }
    }

    // Check if response is OK before parsing
    if (!response.ok) {
      // Try to get error details
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || `Upload failed with status ${response.status}`,
          code: errorData.code || 'UPLOAD_ERROR',
        };
      }
      // Non-JSON response (probably HTML error page)
      return {
        success: false,
        error: `Upload failed with status ${response.status} - API may be unreachable`,
        code: 'UPLOAD_ERROR',
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Upload request failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
      code: 'UPLOAD_ERROR',
    };
  }
}

// ============================================
// VERIFICATIONS API
// ============================================

export interface Verification {
  id: string;
  idType: string;
  verificationType?: string;
  status: 'pending' | 'processing' | 'verified' | 'failed' | 'expired' | 'cancelled';
  confidenceScore: number | null;
  ocrData?: Record<string, any>;
  qrData?: Record<string, any>;
  qrMatchStatus?: 'match' | 'mismatch' | 'no_qr_found' | 'not_scanned';
  blockchainTxHash: string | null;
  blockchainConfirmed: boolean;
  createdAt: string;
  completedAt: string | null;
}

export interface VerificationStats {
  total: number;
  verified: number;
  failed: number;
  pending: number;
  averageConfidence: string | null;
}

export const verifications = {
  /**
   * Create a new verification
   */
  async create(data: {
    idType: string;
    idNumber: string;
    isAssisted?: boolean;
    subjectReference?: string;
    ocrOverrides?: Record<string, string>;
    metadata?: Record<string, any>;
  }): Promise<Verification> {
    const response = await apiRequest<Verification>('/api/verifications', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to create verification');
  },

  /**
   * List verifications
   */
  async list(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{ verifications: Verification[]; pagination: any }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);

    const response = await apiRequest<{ verifications: Verification[]; pagination: any }>(
      `/api/verifications?${searchParams.toString()}`
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to list verifications');
  },

  /**
   * Get verification by ID
   */
  async get(id: string): Promise<Verification> {
    const response = await apiRequest<Verification>(`/api/verifications/${id}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Verification not found');
  },

  /**
   * Get verification statistics
   */
  async getStats(): Promise<VerificationStats> {
    const response = await apiRequest<VerificationStats>('/api/verifications/stats/summary');

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to get statistics');
  },

  /**
   * Upload document images (front + optional back) for server-side processing
   */
  async uploadDocument(
    verificationId: string,
    front: File | Blob,
    back?: File | Blob,
    guestToken?: string,
    handoffSessionId?: string
  ): Promise<{ documentFrontPath: string; documentBackPath?: string }> {
    const formData = new FormData();
    formData.append('document_front', front);
    if (back) formData.append('document_back', back);

    const response = await apiRequestMultipart<{
      documentFrontPath: string;
      documentBackPath?: string;
    }>(`/api/verifications/${verificationId}/documents`, formData, guestToken, handoffSessionId);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to upload documents');
  },

  /**
   * Upload selfie for server-side face comparison
   * @param handoffSessionId - Optional handoff session ID for mobile uploads
   */
  async uploadSelfie(
    verificationId: string,
    selfie: File | Blob,
    guestToken?: string,
    handoffSessionId?: string
  ): Promise<{ selfiePath: string }> {
    const formData = new FormData();
    if (selfie instanceof Blob && !(selfie instanceof File)) {
      formData.append('selfie', selfie, 'selfie.jpg');
    } else {
      formData.append('selfie', selfie);
    }

    const response = await apiRequestMultipart<{ selfiePath: string }>(
      `/api/verifications/${verificationId}/selfie`,
      formData,
      guestToken,
      handoffSessionId
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to upload selfie');
  },

  /**
   * Trigger server-side verification processing (OCR + face match + scoring)
   */
  async process(verificationId: string, guestToken?: string): Promise<{
    status: string;
    confidenceScore: number;
    ocrResult: any;
    faceResult: any;
    authenticityScore: number;
    failureReasons?: string[];
  }> {
    const headers: Record<string, string> = {};
    if (guestToken) headers['X-Guest-Token'] = guestToken;

    const response = await apiRequest<{
      status: string;
      confidenceScore: number;
      ocrResult: any;
      faceResult: any;
      authenticityScore: number;
      failureReasons?: string[];
    }>(`/api/verifications/${verificationId}/process`, {
      method: 'POST',
      headers,
    });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to process verification');
  },

  /**
   * Create a mobile handoff session
   */
  async createHandoffSession(verificationId: string, type: 'liveness' | 'document' = 'liveness'): Promise<{
    sessionId: string;
    secret: string;
  }> {
    const response = await apiRequest<{
      sessionId: string;
      secret: string;
    }>('/api/verifications/handoff', {
      method: 'POST',
      body: JSON.stringify({ verificationId, sessionType: type }),
    });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to create handoff session');
  },

  /**
   * Get handoff session status (polled by desktop)
   */
  async getHandoffStatus(sessionId: string): Promise<{
    status: 'pending' | 'mobile_opened' | 'completed' | 'expired';
    verificationId: string;
    livenessScore?: number;
    faceMatchScore?: number;
    sessionType?: 'liveness' | 'document';
    isAssisted?: boolean;
  }> {
    const response = await apiRequest<{
      status: 'pending' | 'mobile_opened' | 'completed' | 'expired';
      verificationId: string;
      livenessScore?: number;
      faceMatchScore?: number;
      sessionType?: 'liveness' | 'document';
      isAssisted?: boolean;
    }>(`/api/verifications/handoff/${sessionId}`);

    if (response.success && response.data) {
      return {
        status: response.data.status,
        verificationId: response.data.verificationId,
        livenessScore: response.data.livenessScore,
        faceMatchScore: response.data.faceMatchScore,
        sessionType: response.data.sessionType,
        isAssisted: response.data.isAssisted,
      };
    }

    throw new Error(response.error || 'Failed to get handoff status');
  },
};

// ============================================
// CONTACT/DEMO APIs
// ============================================

interface DemoRequest {
  name: string;
  email: string;
  company: string;
  date: string;
  time: string;
  timezone: string;
}

interface SalesInquiry {
  name: string;
  email: string;
  company: string;
  message: string;
}

export async function scheduleDemoRequest(data: DemoRequest) {
  const response = await apiRequest('/api/contact/demo', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.success) {
    throw new Error(response.error || 'Failed to schedule demo');
  }

  return { success: true };
}

export async function submitSalesInquiry(data: SalesInquiry) {
  const response = await apiRequest('/api/contact/sales', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.success) {
    throw new Error(response.error || 'Failed to submit inquiry');
  }

  return { success: true };
}

// Export default client
export default {
  auth,
  verifications,
  request: apiRequest,
};
